import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Footer from '../../components/Footer';
import 'bootstrap-icons/font/bootstrap-icons.css';
import onboardingStorage from '../../utils/onboardingStorage';
import { API_BASE } from '../../config';

// Import illustrations from each step folder
import photo1 from './1.png';
import photo2 from './2.png';
import photo3 from './3.png';
import photo4 from './4.png';
import photo5 from './5.png';
import photo6 from './6.png';
import photo7 from './7.png';

import photo1Dark from './1_dark.png';
import photo2Dark from './2_dark.png';
import photo3Dark from './3_dark.png';
import photo4Dark from './4_dark.png';
import photo5Dark from './5_dark.png';
import photo6Dark from './6_dark.png';
import photo7Dark from './7_dark.png';

const STEPS = {
  1: {
    question: 'Какое направление досуга тебе ближе?',
    illustration: photo1,
    illustrationDark: photo1Dark,
    options: [
      { id: 101, label: 'Путешествия' },
      { id: 106, label: 'Спорт и активный отдых' },
      { id: 102, label: 'Кино и сериалы' },
      { id: 105, label: 'Видеоигры и гейминг' },
      { id: 104, label: 'Чтение и литература' },
      { id: 103, label: 'Музыка и концерты' },
      { id: 107, label: 'Кулинария и гастрономия' },
      { id: 108, label: 'Другое' },
    ]
  },
  2: {
    question: 'Как предпочитаешь проводить свободное время?',
    illustration: photo2,
    illustrationDark: photo2Dark,
    options: [
      { id: 201, label: 'Посещение мероприятий\n(концерты, театр, выставки)' },
      { id: 203, label: 'Спокойно\n(дом, книги, кино)' },
      { id: 202, label: 'Активно\n(походы и велосипеды)' },
      { id: 205, label: 'Встречи с друзьями' },
      { id: 204, label: 'Создание цифрового контента\n(фото, видео, монтаж)' },
      { id: 206, label: 'Путешествия и экскурсии' },
      { id: 207, label: 'Другое' },
    ]
  },
  3: {
    question: 'Какую атмосферу общения ты предпочитаешь?',
    illustration: photo3,
    illustrationDark: photo3Dark,
    options: [
      { id: 303, label: 'Обсуждение хобби' },
      { id: 304, label: 'Партнёрские отношения' },
      { id: 305, label: 'Философская и созерцательная' },
      { id: 301, label: 'Лёгкий флирт' },
      { id: 306, label: 'Непринуждённая' },
      { id: 302, label: 'Глубокие разговоры' },
      { id: 307, label: 'Другое' },
    ]
  },
  4: {
    question: 'Что для тебя главное в жизни?',
    illustration: photo4,
    illustrationDark: photo4Dark,
    options: [
      { id: 401, label: 'Саморазвитие и новые знания' },
      { id: 404, label: 'Семья и друзья' },
      { id: 402, label: 'Успех и признание' },
      { id: 405, label: 'Творчество' },
      { id: 403, label: 'Помощь другим' },
      { id: 406, label: 'Другое' },
    ]
  },
  5: {
    question: 'Какой формат знакомства тебе предпочтителен?',
    illustration: photo5,
    illustrationDark: photo5Dark,
    options: [
      { id: 503, label: 'Совместные занятия' },
      { id: 504, label: 'Групповые мероприятия' },
      { id: 505, label: 'Текстовые сообщения' },
      { id: 506, label: 'Случайные встречи' },
      { id: 501, label: 'Онлайн-общение' },
      { id: 502, label: 'Встречи в живую' },
      { id: 507, label: 'Другое' },
    ]
  },
  6: {
    question: 'Какие из этих привычек тебе ближе?',
    illustration: photo6,
    illustrationDark: photo6Dark,
    options: [
      { id: 601, label: 'Чёткое планирование всего' },
      { id: 605, label: 'Ночная активность' },
      { id: 607, label: 'Сознательное время для отдыха' },
      { id: 604, label: 'Ранний подъём' },
      { id: 606, label: 'Постоянное изучение нового' },
      { id: 603, label: 'Спорт и ЗОЖ' },
      { id: 602, label: 'Импровизация и спонтанные решения' },
      { id: 608, label: 'Другое' },
    ]
  },
  7: {
    question: 'Какие темы для разговора тебе интересны?',
    illustration: photo7,
    illustrationDark: photo7Dark,
    options: [
      { id: 701, label: 'Искусство и культура' },
      { id: 702, label: 'Наука и технологии' },
      { id: 703, label: 'Животные и природа' },
      { id: 706, label: 'Дружеское общение' },
      { id: 704, label: 'Кино и музыка' },
      { id: 705, label: 'Психология и саморазвитие' },
      { id: 707, label: 'Другое' },
    ]
  }
};

function NewQuestionnaire() {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Extract and clamp the step from URL search parameters to keep it robust and safe
  const rawStep = parseInt(searchParams.get('step') || '1', 10);
  const currentStep = Math.max(1, Math.min(7, rawStep));

  const [isFirstLoad, setIsFirstLoad] = useState(true);

  // Auto-correct invalid URL step queries immediately
  useEffect(() => {
    if (rawStep !== currentStep) {
      setSearchParams({ step: currentStep });
    }
  }, [rawStep, currentStep, setSearchParams]);

  // Load saved answers whenever the current step updates, or after first-time backend load
  useEffect(() => {
    window.scrollTo(0, 0);
    const answers = onboardingStorage.getAnswers() || {};
    let saved = answers[currentStep] || [];

    // Retain legacy habit sanitization (filtering out 601) on Step 6
    if (currentStep === 6) {
      const originalLength = saved.length;
      saved = saved.filter(id => id !== 601);
      if (saved.length !== originalLength) {
        onboardingStorage.setAnswer(6, saved);
      }
    }

    setSelectedOptions(saved);
  }, [currentStep, isFirstLoad]);

  // Proactively pull existing selections from the backend on mount (Synchronizing Server State!)
  useEffect(() => {
    const syncServerAnswers = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const fetchStepAnswers = async (stepId) => {
          try {
            const resp = await fetch(`${API_BASE}/api/Onboarding/answers/${stepId}`, { headers });
            if (resp.ok) {
              const data = await resp.json();
              return data?.selectedOptionIds || [];
            }
          } catch (e) {
            console.warn(`Error fetching answer for step ${stepId}:`, e);
          }
          return [];
        };

        const steps = [1, 2, 3, 4, 5, 6, 7];
        const allResults = await Promise.all(steps.map(step => fetchStepAnswers(step)));

        // Merge any server results into onboardingStorage
        steps.forEach((step, idx) => {
          const serverSelected = allResults[idx];
          if (Array.isArray(serverSelected) && serverSelected.length > 0) {
            onboardingStorage.setAnswer(step, serverSelected);
          }
        });

        // Trigger selection reload
        setIsFirstLoad(false);
      } catch (err) {
        console.warn('Failed to sync existing onboarding answers from server:', err);
      }
    };

    syncServerAnswers();
  }, []);

  const toggleOption = (id) => {
    if (selectedOptions.includes(id)) {
      setSelectedOptions(selectedOptions.filter(item => item !== id));
    } else {
      setSelectedOptions([...selectedOptions, id]);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setSearchParams({ step: currentStep - 1 });
    } else {
      navigate(-1);
    }
  };

  const handleNext = async () => {
    // Save current step selections to state cache
    onboardingStorage.setAnswer(currentStep, selectedOptions);

    if (currentStep < 7) {
      setSearchParams({ step: currentStep + 1 });
    } else {
      // Step 7 logic: Submit all answers
      const raw = onboardingStorage.getAnswers() || {};
      const payload = {};
      Object.keys(raw).forEach(k => {
        const idx = Number(k);
        if (!Number.isNaN(idx)) {
          payload[idx] = Array.isArray(raw[k]) ? raw[k].map(v => Number(v)) : [];
        }
      });
      console.log('Submitting onboarding payload:', payload);

      try {
        const headers = { 'Content-Type': 'application/json' };
        const token = localStorage.getItem('token');
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const resp = await fetch(`${API_BASE}/api/Onboarding/answers/by-index`, {
          method: 'PUT',
          headers,
          credentials: token ? 'omit' : 'include',
          body: JSON.stringify(payload)
        });

        if (!resp.ok) {
          const text = await resp.text();
          alert(`Ошибка отправки ответов: ${resp.status} ${resp.statusText}\n${text}`);
          return;
        }

        // Successfully saved onboarding options
        onboardingStorage.clearAnswers();

        // Sync fresh profile details to localStorage
        try {
          if (token) {
            const profileResp = await fetch(`${API_BASE}/api/Profile/me`, {
              method: 'GET',
              headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (profileResp.ok) {
              const profile = await profileResp.json();
              localStorage.setItem('firstName', profile.firstName || '');
              localStorage.setItem('lastName', profile.lastName || '');
              const nick = profile.nickname ? profile.nickname.replace(/^@+/, '') : '';
              localStorage.setItem('username', nick);
              localStorage.setItem('email', profile.email || '');
              if (profile.city) localStorage.setItem('city', profile.city);
              if (profile.jobTitle) localStorage.setItem('profession', profile.jobTitle);
              if (profile.birthDate) localStorage.setItem('birthDate', profile.birthDate);
              if (profile.bio) localStorage.setItem('bio', profile.bio);
            }
          }
        } catch (e) {
          console.warn('Failed to refresh profile after onboarding', e);
        }

        navigate('/edit-profile');
      } catch (err) {
        console.error('Submit onboarding error', err);
        alert('Ошибка соединения с сервером при отправке ответов.');
      }
    }
  };

  const activeStepConfig = STEPS[currentStep] || STEPS[1];

  return (
    <div className="questionnaire-page min-h-screen flex flex-col font-['TT_Norms_Pro']">
      <main className="questionnaire-page__main flex-grow mx-auto">
        {/* Back Button */}
        <div className="questionnaire-page__back">
          <button 
            onClick={handleBack} 
            className="questionnaire-page__back-button"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Назад
          </button>
        </div>

        {/* Step Question */}
        <h1 className="questionnaire-page__title">
          {activeStepConfig.question}
        </h1>

          {/* Interactive Option Bricks */}
          <div className="questionnaire-page__options-grid">
            {activeStepConfig.options.map(option => (
              <button
                key={option.id}
                onClick={() => toggleOption(option.id)}
                className={`questionnaire-page__option-brick ${
                  selectedOptions.includes(option.id) ? 'is-selected' : ''
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          {/* Illustration and Controls Section */}
          <div className="questionnaire-page__footer-row">
            {/* Step Illustration */}
            <img
              src={activeStepConfig.illustration}
              alt={`Step ${currentStep} Illustration`}
              className={`questionnaire-page__illustration questionnaire-page__illustration--step${currentStep} light-only`}
            />
            <img
              src={activeStepConfig.illustrationDark}
              alt={`Step ${currentStep} Illustration`}
              className={`questionnaire-page__illustration questionnaire-page__illustration--step${currentStep} dark-only`}
            />

            {/* Step Controls */}
            <div className="questionnaire-page__controls-wrap">
              <span className="questionnaire-page__hint">
                Выберите от&nbsp;1 или более вариантов ответа
              </span>
              <button
                disabled={selectedOptions.length === 0}
                onClick={handleNext}
                className="questionnaire-page__next-button"
              >
                {currentStep === 7 ? 'Завершить' : 'Далее'}
              </button>
            </div>
          </div>
      </main>
      <Footer />
    </div>
  );
}

export default NewQuestionnaire;
